import { z } from 'zod'
import { requiredText } from '../../../lib/forms/rules'
import { utf8Length } from '../../../lib/textBytes'

/**
 * `NAME` kolon genişliği — **BAYT** (`VARCHAR2(255)`, `CHAR_USED = B`, charset `AL32UTF8`; canlı şemada
 * doğrulandı). Girdi kutusunun `maxLength`'i olarak da kullanılır: ASCII'de bayt = karakter, Türkçe
 * harflerde sınır daha erken dolar ve bunu {@link byteLength} kuralı yakalar.
 */
export const NAME_MAX_LEN = 255

/** `PREFIX` kolon genişliği — **BAYT** (`VARCHAR2(10)` = 10 bayt; aynı gerekçe). */
export const PREFIX_MAX_LEN = 10

/**
 * UTF-8 bayt uzunluğu. Kolonlar bayt semantiğinde olduğu için sınır karakter değil **bayt**tır: Türkçe
 * bir harf (`Ö`, `Ş`, `ı`…) 2 bayt yer kaplar. Karakter saymak, 10 karakterin altında kalan ama 10 baytı
 * aşan bir ön eki (ör. `ÖĞÜŞÇI` → 11 bayt) sunucuya gönderir ve orada `ORA-12899` → 500 olur; bu kural
 * hatayı **alan altında** gösterir. Backend `PrefixAdminService.exceedsBytes` ile birebir aynı kural.
 *
 * Ölçüm `lib/textBytes.ts`'e devredilir (tek kaynak); buradaki ad, aşağıdaki iki kuralın okunurluğu için
 * korunur.
 */
const byteLength = utf8Length

/**
 * Ön ek biçim deseni — backend `PrefixUpdateDto.PREFIX_PATTERN` ile **birebir aynı** metin:
 * yalnız harf/rakam ve aralarında **tek** `-`; başta/sonda `-` ve çift `--` kabul edilmez, boşluk da
 * (harf/rakam sınıfı dışında kaldığından) reddedilir. Unicode sınıfları (`\p{L}` → `u` bayrağı şart)
 * kullanılır ki Türkçe harfler geçerli kalsın.
 *
 * **Bu desen iki yerde yaşar** (Zod + Java Bean Validation); biri değişirse **ikisi birlikte**
 * güncellenir — servis testi ve ekran testi aynı örneklerle bu bağı korur.
 */
export const PREFIX_PATTERN = /^[\p{L}\p{N}]+(?:-[\p{L}\p{N}]+)*$/u

/** Ön ek tanımı düzenleme form şeması (056): ad ve ön ek **ikisi de zorunlu** (DB'de `NOT NULL`). */
export const prefixFormSchema = z.object({
  // Bayt kuralı `requiredText`'in `maxBytes` parametresiyle DEĞİL, aşağıdaki `refine` ile verilir:
  // buradaki mesaj alana özgüdür ("en fazla 255 bayt; Türkçe harfler 2 bayt yer kaplar") ve paylaşılan
  // `validation.maxBytes` metninden daha açıklayıcıdır. İkisini birden koymak, Zod issue'ları sırayla
  // ürettiği için genel mesajı öne geçirip bu metni ölü bırakırdı.
  name: requiredText(NAME_MAX_LEN).refine(
    (v) => byteLength(v.trim()) <= NAME_MAX_LEN,
    'prefixes.errors.nameTooLong',
  ),
  prefix: requiredText(PREFIX_MAX_LEN)
    .regex(PREFIX_PATTERN, 'prefixes.errors.prefixFormat')
    .refine((v) => byteLength(v.trim()) <= PREFIX_MAX_LEN, 'prefixes.errors.prefixTooLong'),
})

export type PrefixFormValues = z.infer<typeof prefixFormSchema>
